/** @module searchUtils */
import _get from 'lodash/get';
import { templateIdForFacets, facetsOrder } from '../../modules/search/customer/default/search.constants';
import { getFormattedItems, isSearchEntity } from './layout.utils';
import { getContentImageOptions } from './layout.image.utils';
import { t } from '../../modules/i18n';

export const isAlgoliaRequest = ({ entityType, entityId, queryParameters }) =>
  isSearchEntity(entityType, entityId) && queryParameters && queryParameters.query;

const prepareActionAnalyticsItems = items => {
  const preparedItems = items.map(({ action, ...rest }) => {
    const googleAnalytics = _get(action, 'analytics.googleAnalytics', {});
    const newGoogleAnalytics = {
      ...googleAnalytics,
      eventAction: googleAnalytics.eventAction || 'program',
      eventCategory: googleAnalytics.eventCategory || 'search',
    };

    const tealium = _get(action, 'analytics.tealium', {});
    const newTealium = {
      ...tealium,
      event_action: tealium.event_action || 'program',
      event_category: tealium.event_category || 'search',
    };

    return {
      ...rest,
      action: {
        ...action,
        analytics: {
          ...action.analytics,
          googleAnalytics: newGoogleAnalytics,
          tealium: newTealium,
        },
      },
    };
  });

  return preparedItems;
};

const prepareSearchAnalytics = facet => {
  const preparedItems = prepareActionAnalyticsItems(facet.content.items);

  return { ...facet, content: { ...facet.content, items: preparedItems } };
};

/**
 * Map hits to match the format of a Layout block.
 *
 * @param {Object[]} hits
 * @param {string} facet
 * @param {string} screenSize
 * @returns {Object}
 */
export const mapSearchHits = (hits, facet, screenSize, contentTemplatesOptions) => {
  const flattenItems = hits.map(({ item }) => item);
  const contentTemplateId = templateIdForFacets[facet];
  const imageOptions = getContentImageOptions(contentTemplateId, contentTemplatesOptions, screenSize);

  const forceGhostServiceIcon =
    contentTemplateId && (contentTemplateId.startsWith('Card') || contentTemplateId.startsWith('Poster'));

  return {
    id: facet,
    content: {
      contentTemplateId,
      pagination: {
        itemsPerPage: flattenItems.length,
        nextPage: null,
        totalItems: flattenItems.length,
      },
      items: getFormattedItems(flattenItems, imageOptions, forceGhostServiceIcon),
    },
  };
};

/**
 * Transform the results to match the block list engine:
 * get the results for each facet, and sort them.
 *
 * @param {Object} results
 * @returns {Object[]}
 */
export const getResultsAsBlocks = results => {
  const blocks = results.filter(result => Boolean(_get(result, 'content.items', []).length));

  const sortedBlocks = facetsOrder.reduce((acc, facetId) => {
    const filteredFacet = blocks.find(block => block.id === facetId);
    if (!filteredFacet) {
      return acc;
    }

    acc.push({
      ...prepareSearchAnalytics(filteredFacet),
      title: { long: t(`search.${filteredFacet.id}`, { numVideos: filteredFacet.content.items.length }) },
      blockTemplateId: 'Grid',
    });

    return acc;
  }, []);

  return sortedBlocks;
};
